import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import classNames from "classnames";
import moment from "moment";

import { PreviewText } from "@highnote/preview-text/src";
import { FileEntity, Space, Track } from "@highnote/server/src/core/entities";
import { formatDuration } from "@highnote/server/src/core/shared-util";
import { highnote } from "@highnote/server/src/sdk";
import { ProgressBar } from "App/common/ProgressBar";
import { StatusIndicator } from "App/common/StatusIndicator";
import { ThumbnailPreview } from "App/common/ThumbnailPreview";
import { ReactComponent as SuccessSVG } from "App/common/icons-v2/check.svg";
import { ReactComponent as CloseSVG } from "App/common/icons-v2/close.svg";
import { ReactComponent as DefaultSpaceFolderSVG } from "App/common/icons-v2/default-space-folder.svg";
import { ReactComponent as DefaultUploadFileSVG } from "App/common/icons-v2/default-upload-file.svg";
import { ReactComponent as ExternalLinkSVG } from "App/common/icons-v2/external-link.svg";
import { ReactComponent as FileReduceFillSVG } from "App/common/icons-v2/file-reduce-fill.svg";
import { ReactComponent as FolderFillSVG } from "App/common/icons-v2/folder-fill.svg";
import { ConnectedUserAvatar } from "App/common/UserAvatar/UserAvatar";
import { ReactComponent as MusicNoteSVG } from "App/common/icons-v2/music-fill.svg";
import { ReactComponent as TrackVersionSVG } from "App/common/icons-v2/waveform.svg";
import { LoadingSpinner } from "App/common/icons/LoadingSpinner";
import { useConfirmation } from "App/common/useConfirmation";
import { useSpaceContext } from "App/common/useSpace";
import { useViewport } from "App/common/useViewport";
import { FileUpload, useFiles } from "App/components/useFiles";
import { Button, BUTTON_SIZE, BUTTON_THEME } from "App/core/Button";
import { Tooltip } from "App/core/Tooltip";
import { DEFAULT_TRACK_ARTWORK_PATH } from "App/modules/consts";
import { formatCount, formatFileSize, getFaviconUrl } from "App/modules/utils";
import {
  getNumSpacesInSpace,
  getNumTracksInSpace,
} from "App/services/firebase/entities/helpers";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";
import { SpaceTrackTile } from "App/store/tracks/buildSpaceTrackTile";
import { useGlobalTracks } from "App/store/tracks/useGlobalTracks";
import {
  GlobalAudioCurrentFormattedTime,
  useTrackRow,
} from "../GlobalAudioPlayer/GlobalAudioPlayer";
import { TrackVersionStar } from "../TrackEditor/TrackVersions/TrackVersionStar";
import { useSpaces } from "../useEntities";
import { useTrackFiles } from "../useEntities/useTrack";
import { usePublicUser } from "../PublicUsersContext";
import { EntityMetadata } from "./EntityMetadata";
import {
  ENTITY_PREVIEW_METADATA,
  ENTITY_TYPE,
  EntityData,
  Version,
} from "./config";
import "./EntityPreview.scss";

const downloadUrlCache: Record<string, string> = {};

// The server authorizes a file download against the *viewing* spaceId, so
// the same file id can resolve to a different URL (or a denial) depending on
// which space it's requested from. The cache key must therefore be the
// (fileId, viewingSpaceId) pair, not the fileId alone — matching mobile's
// `fileId::spaceId` convention.
//
// "No viewing space" (routes outside `/space/...` — Library, Discover,
// settings, where the server falls back to authorizing against the track's
// origin) is a real, distinct case from "viewing space X", and an entry
// cached under one must never satisfy a lookup for the other. Naively
// interpolating `viewingSpaceId` into the template when it's `undefined`
// would silently produce the literal string `"undefined"` — a real,
// id-shaped value indistinguishable from an actual (if wildly unlikely)
// spaceId of `"undefined"`. Firestore document ids are never the empty
// string, so `""` is a sentinel for "no viewing space" that cannot collide
// with any real spaceId.
const getDownloadUrlCacheKey = (fileId: string, viewingSpaceId?: string) =>
  `${fileId}::${viewingSpaceId ?? ""}`;

export const getFallbackArtwork = (row: EntityData) => {
  if (row.type === ENTITY_TYPE.TRACK) {
    return DEFAULT_TRACK_ARTWORK_PATH;
  }

  if (row.type === ENTITY_TYPE.SPACE) {
    return <DefaultSpaceFolderSVG />;
  }

  if (row.type === ENTITY_TYPE.TRACK_VERSION) {
    return DEFAULT_TRACK_ARTWORK_PATH;
  }

  if (row.type === ENTITY_TYPE.FILE) {
    const file = row.entity as FileEntity;

    if ((file as Version).isVersion) {
      return <TrackVersionSVG />;
    }

    if (file.url) {
      return "/public/icons/default-file-url.svg";
    }

    if (String(file.fileType).includes("image/")) {
      return "/public/icons/default-file-img.png";
    }

    const specialExtensions = ["mp3", "ogg", "aif", "mp4", "zip", "wav"];
    const ext = specialExtensions.find((e) => file.fileName?.endsWith(e));

    if (ext) {
      return `/public/icons/default-file-${ext}.svg`;
    }
  }

  return <DefaultUploadFileSVG />;
};

const getCachedArtwork = (
  artworkFile?: string,
  viewingSpaceId?: string,
  url?: string,
) => {
  if (artworkFile) {
    return downloadUrlCache[
      getDownloadUrlCacheKey(artworkFile, viewingSpaceId)
    ];
  }

  if (url) {
    return getFaviconUrl(url) || "/public/icons/default-file-url.svg";
  }
};

export const useArtworkUrl = ({ row }: { row: EntityData }) => {
  const { getDownloadUrl } = useFiles();
  const entity = row.entity as Track | Space;
  const file = row.entity as FileEntity;
  const { entities: spaces } = useSpaces();
  const { space } = useSpaceContext();
  // The viewing space comes from the ROW, not the route. This column
  // renders in every EntityTable — including the move/add dialogs' lists
  // of tracks from OTHER spaces, on a space route. For those, the route's
  // space is a space the track is not in: GET_FILES filters the id out and
  // the artwork silently disappears, where no viewing space at all would
  // have fallen back to the origin and worked. Rows listed in a space
  // carry that space (see `EntityData.viewingSpaceId`); the rest carry
  // nothing. Distinct from the TRACK's own origin spaceId below, which is
  // used only to look up the parent space's fallback artwork.
  const viewingSpaceId = row.viewingSpaceId;
  let parentArtworkFile: string;
  if (row.type === ENTITY_TYPE.TRACK) {
    const spaceId = (entity as Track).spaceId;
    if (spaceId) {
      parentArtworkFile = spaces.find((s) => s.id === spaceId)?.artworkFile;
    }
    if (!parentArtworkFile) {
      parentArtworkFile = space?.artworkFile;
    }
  }
  const artworkFile = entity.artworkFile || parentArtworkFile;
  const [source, setSource] = useState<string>(
    getCachedArtwork(artworkFile, viewingSpaceId, file.url),
  );

  useEffect(() => {
    let unmounted: boolean;

    const cachedUrl = getCachedArtwork(artworkFile, viewingSpaceId, file.url);
    if (cachedUrl) {
      setSource(cachedUrl);
      return;
    }

    if (!artworkFile) {
      setSource(undefined);
      return;
    }

    // `artworkFile` can be TRACK-parented (a track's own artwork is stamped
    // TRACK_ATTACHMENT, `tracks.ts:606-607`) and is therefore subject to
    // GET_FILES' row-level authorization against the VIEWING space
    // (`fileAuthorization.ts`'s TRACK_VERSION/TRACK_ATTACHMENT arm) — the
    // same `viewingSpaceId` already used for the cache key and
    // `getDownloadUrl` below. Omitting it here silently authorized only
    // against the track's origin, so GET_FILES filtered the id out of a
    // 200 response for a borrowing-only member with no visible error.
    highnote
      .getFiles({
        ids: [artworkFile],
        ...(viewingSpaceId ? { spaceId: viewingSpaceId } : {}),
      })
      .then(async (files) => {
        const artworkFileEntity = files.find((f) => f.id === artworkFile);
        const url = await getDownloadUrl(artworkFileEntity, viewingSpaceId);
        if (unmounted) return;
        downloadUrlCache[getDownloadUrlCacheKey(artworkFile, viewingSpaceId)] =
          url;
        setSource(url);
      });

    return () => {
      unmounted = true;
    };
  }, [row.type, artworkFile, file.url, viewingSpaceId]);

  return {
    source: source,
    fallback: getFallbackArtwork(row),
  };
};

export const TrackTitle = ({
  track,
  onClick,
}: {
  track: Track;
  onClick: () => void;
}) => {
  return (
    <span
      className={classNames("TrackTitle", { clickable: !!onClick })}
      onClick={onClick}
    >
      <PreviewText>{track.title}</PreviewText>
    </span>
  );
};

export const TrackDescription = ({
  track,
  onClick,
  showTrackTime,
}: {
  track: Track;
  onClick?: () => void;
  showTrackTime?: boolean;
}) => {
  const { currentTrackPageVersion } = useTrackFiles(track);
  const [spaceStr, setSpaceStr] = useState<string>();
  const { spaceId } = useSpaceContext();
  // A row-backed tile (space-tracks-v1 join row, before/without a canonical
  // Track doc) has no `versionFilesV2` at all — that array only exists on
  // the canonical doc. Prefer it when present (it's always an array, even
  // briefly empty, on a real Track); fall back to the row's own mirrored
  // `versionCount` only when it's structurally absent, never replacing a
  // real (possibly empty) canonical array.
  const versionCount = track.versionFilesV2
    ? track.versionFilesV2.length
    : (track as SpaceTrackTile).versionCount ?? 0;
  const versionsStr =
    currentTrackPageVersion?.name ?? formatCount(versionCount, "Version");
  useEffect(() => {
    let unmounted: boolean;
    if (!track?.spaceId || track.spaceId === spaceId) {
      setSpaceStr(undefined);
      return;
    }

    /**
     * A borrowed track never labels itself with its origin.
     *
     * This effect exists for a rare case: a track MOVED into a space you
     * cannot read, where naming that space is useful context. Track sharing
     * makes `track.spaceId !== spaceId` the NORMAL state of every borrowed
     * tile instead, and for those the origin is a space the viewer usually
     * has no access to — so the fetch is denied, the label never appears,
     * and the catch below logs an error on every such tile. Observed firing
     * repeatedly for a single borrowed track.
     *
     * Product decision: borrowed tiles do not show where they came from.
     * "Also in Y, Z" lives on the track page instead, via GET_TRACK's
     * `sharedIn`, which the server already filters to spaces the caller can
     * actually read. So skip the lookup at its source rather than swallow
     * its rejection — nothing is fetched, and nothing is logged.
     */
    if ((track as SpaceTrackTile).isRowBacked) {
      setSpaceStr(undefined);
      return;
    }

    highnote
      .getSpace({ id: track.spaceId, fromCache: true })
      .then((space) => {
        if (unmounted) return;
        setSpaceStr(` • ${space.name}`);
      })
      .catch((e) => {
        // Track you uploaded can belong to a space you don't have access to.
        // ie. User 1 has a space, and makes User 2 admin. User 2 creates their own space
        // that User 1 is not apart of yet, and moves a track/entity from User 1 Space -> User 2 Space.
        // User 1 will get an error for fetching space data of that new track's location.
        console.error("Error getting parent space", e);
      });

    return () => {
      unmounted = true;
    };
  }, [track?.spaceId, spaceId]);

  return (
    <span
      className={classNames("track-description", { showTrackTime })}
      data-clickable={!!onClick}
      onClick={() => onClick && onClick()}
    >
      {showTrackTime && (
        <span className="track-description-time">
          <GlobalAudioCurrentFormattedTime />
        </span>
      )}
      <span className="track-description-main">
        {versionsStr}
        {spaceStr}
      </span>
    </span>
  );
};

const TrackVersionDescription = ({
  version,
}: {
  version: Version | FileEntity;
}) => {
  const fileDate = moment(version.createdAt).format("MMMM D, YYYY");
  return (
    <div className="entity-preview-metadata">
      <span className="entity-preview-metadata-icon">
        <TrackVersionSVG />
      </span>
      {fileDate}
      <span className="dot-text-divider" />
      {version.metadata?.duration
        ? formatDuration(version.metadata?.duration, true)
        : "--:--"}
    </div>
  );
};

const SpaceDescription = ({ space }: { space: Space }) => {
  const { globalTracks, globalTracksWatchers } = useGlobalTracks();
  const { globalSpaces, hasGlobalSpace } = useGlobalSpaces();
  const [description, setDescription] = useState<string>();

  const childTracks = globalTracks.childTracks.get(space?.id);
  const childSpaces = globalSpaces.childSpaces.get(space?.id);
  const hasWatcherData =
    (globalTracksWatchers.has(space?.id) || hasGlobalSpace(space?.id)) &&
    (childTracks !== undefined || childSpaces !== undefined);

  useEffect(() => {
    let unmounted: boolean;

    if (!space) {
      setDescription("");
      return;
    }

    if (hasWatcherData) {
      const totalChildTracksCount = childTracks ? childTracks.length : 0;
      const totalChildSpacesCount = childSpaces ? childSpaces.length : 0;
      const totalChildItemsCount =
        totalChildTracksCount +
        totalChildSpacesCount +
        (space.files || []).length; // space.files represents file attachments within the space
      setDescription(`${formatCount(totalChildItemsCount, "Item")}`);
    } else {
      const itemsPromise = Promise.all([
        getNumTracksInSpace(space),
        getNumSpacesInSpace(space.id),
      ]).then((counts) => {
        const totalCount =
          counts.reduce((acc, count) => acc + count, 0) +
          (space.files || []).length;
        return `${formatCount(totalCount || 0, "Item")}`;
      });

      itemsPromise.then((summary) => {
        if (unmounted) return;
        setDescription(summary);
      });
    }

    return () => {
      unmounted = true;
    };
  }, [space?.id, hasWatcherData, childTracks?.length, childSpaces?.length]);

  return <>{description}</>;
};

// The host of a URL, stripped of a leading "www." (e.g. "example.com").
// Returns the raw string if it can't be parsed as a URL.
const getUrlDomain = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

export const EntityPreview = ({
  row,
  hideThumbnail,
  className,
}: {
  row: EntityData;
  hideThumbnail?: boolean;
  className?: string;
}) => {
  const { isMobile } = useViewport();
  const { source, fallback } = useArtworkUrl({ row });
  const { confirm, renderConfirmation } = useConfirmation();
  const { fetchUser: fetchCreatedByUser, users: createdByUsers } =
    usePublicUser();

  let title: string | React.ReactNode;
  let data: React.ReactNode;

  const space = row.entity as Space;
  const track = row.entity as Track;
  const file = row.entity as FileEntity;
  const version = (file as Version).isVersion && (file as Version);
  const upload = row.entity as FileUpload;

  const isSpace = row.type === ENTITY_TYPE.SPACE;
  const isTrack = row.type === ENTITY_TYPE.TRACK;

  // Root-level creator name for the byline, resolved once for every entity
  // type. Precedence: track.artistName (tracks only) → entity.createdByName →
  // the fetched public user's name. `createdBy` and `createdByName` live on the
  // shared entity base, so this works for spaces, tracks, and file attachments.
  const createdBy = (row.entity as { createdBy?: string }).createdBy;
  const createdByName = (row.entity as { createdByName?: string })
    .createdByName;
  const artistName = isTrack ? track.artistName : undefined;
  const denormalizedCreatorName = artistName || createdByName || null;

  // Fetch the public user only when the name isn't already denormalized on the
  // entity (e.g. older spaces/tracks/files).
  const creatorIdToFetch =
    !denormalizedCreatorName && createdBy ? createdBy : null;
  useEffect(() => {
    if (creatorIdToFetch) fetchCreatedByUser(creatorIdToFetch);
  }, [creatorIdToFetch, fetchCreatedByUser]);

  const creatorName =
    denormalizedCreatorName ||
    (createdBy ? createdByUsers[createdBy]?.name : null) ||
    null;

  // Render a sequence of metadata pieces with dot dividers between them.
  // Falsy pieces are filtered out so we don't end up with leading/trailing
  // dividers or doubled-up dividers when something is missing.
  // The icon, when included, is always rendered first and has no divider
  // after it.
  const renderMetadata = (
    icon: React.ReactNode | null,
    pieces: Array<React.ReactNode>,
  ) => {
    const visible = pieces.filter(Boolean);
    return (
      <div className="entity-preview-metadata">
        {icon && <span className="entity-preview-metadata-icon">{icon}</span>}
        {visible.map((piece, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="dot-text-divider" />}
            {piece}
          </React.Fragment>
        ))}
      </div>
    );
  };

  if (isSpace) {
    title = space.name;
    const memberCount = space.memberCount ?? 0;

    const fields = row.entityPreviewMetadata ?? [
      ENTITY_PREVIEW_METADATA.ICON,
      ENTITY_PREVIEW_METADATA.CREATED_AT,
      ENTITY_PREVIEW_METADATA.DESCRIPTION,
    ];
    const icon = fields.includes(ENTITY_PREVIEW_METADATA.CREATED_BY_AVATAR) ? (
      <ConnectedUserAvatar
        userId={space.createdBy}
        alias={creatorName ?? undefined}
      />
    ) : fields.includes(ENTITY_PREVIEW_METADATA.ICON) ? (
      <FolderFillSVG />
    ) : null;
    const pieces = fields.map((field) => {
      switch (field) {
        case ENTITY_PREVIEW_METADATA.CREATOR_NAME:
          return creatorName;
        case ENTITY_PREVIEW_METADATA.MEMBER_COUNT:
          return `${memberCount} ${memberCount === 1 ? "member" : "members"}`;
        case ENTITY_PREVIEW_METADATA.CREATED_AT:
          return moment(space.createdAt).format("MMMM D, YYYY");
        case ENTITY_PREVIEW_METADATA.DESCRIPTION:
          return <SpaceDescription space={space} />;
        default:
          return null;
      }
    });

    data = renderMetadata(icon, pieces);
  }

  if (row.type === ENTITY_TYPE.TRACK) {
    title = track.title;

    const fields = row.entityPreviewMetadata ?? [
      ENTITY_PREVIEW_METADATA.ICON,
      ENTITY_PREVIEW_METADATA.CREATOR_NAME,
      ENTITY_PREVIEW_METADATA.CREATED_AT,
      ENTITY_PREVIEW_METADATA.ITEM_COUNT,
      ENTITY_PREVIEW_METADATA.DESCRIPTION,
    ];
    const icon = fields.includes(ENTITY_PREVIEW_METADATA.CREATED_BY_AVATAR) ? (
      <ConnectedUserAvatar
        userId={track.createdBy}
        alias={creatorName ?? undefined}
      />
    ) : fields.includes(ENTITY_PREVIEW_METADATA.ICON) ? (
      <MusicNoteSVG />
    ) : null;
    const pieces = fields.map((field) => {
      switch (field) {
        case ENTITY_PREVIEW_METADATA.CREATOR_NAME:
          return creatorName ? (
            <span className="entity-preview-metadata-artist-name">
              {creatorName}
            </span>
          ) : null;
        case ENTITY_PREVIEW_METADATA.CREATED_AT:
          return moment(track.createdAt).format("MMMM D, YYYY");
        case ENTITY_PREVIEW_METADATA.ITEM_COUNT:
          return (
            <EntityMetadata row={row} className="entity-preview-metadata" />
          );
        case ENTITY_PREVIEW_METADATA.DESCRIPTION:
          return <TrackDescription track={track} />;
        default:
          return null;
      }
    });

    data = renderMetadata(icon, pieces);
  }

  if (row.type === ENTITY_TYPE.TRACK_VERSION) {
    title = version.name || "";
    data = <TrackVersionDescription version={version} />;
  }

  if (row.type === ENTITY_TYPE.FILE) {
    title = file.name || file.fileName;
    const fileDate = moment(file.createdAt).format("MMMM D, YYYY");
    const isBookmark = !!file.url;

    if (version) {
      // Track-version files keep their own render (star title + duration),
      // separate from the two-piece attachment byline below.
      title = (
        <span className="version-title">
          <PreviewText>{file.name || file.fileName}</PreviewText>
          <TrackVersionStar version={version} />
        </span>
      );

      data = <TrackVersionDescription version={version} />;
    } else {
      const fields = row.entityPreviewMetadata ?? [
        ENTITY_PREVIEW_METADATA.ICON,
        ENTITY_PREVIEW_METADATA.CREATED_AT,
        isBookmark
          ? ENTITY_PREVIEW_METADATA.DOMAIN
          : ENTITY_PREVIEW_METADATA.FILE_SIZE,
      ];
      const icon = fields.includes(
        ENTITY_PREVIEW_METADATA.CREATED_BY_AVATAR,
      ) ? (
        <ConnectedUserAvatar
          userId={file.createdBy}
          alias={creatorName ?? undefined}
        />
      ) : fields.includes(ENTITY_PREVIEW_METADATA.ICON) ? (
        isBookmark ? (
          <ExternalLinkSVG />
        ) : (
          <FileReduceFillSVG />
        )
      ) : null;
      const pieces = fields.map((field) => {
        switch (field) {
          case ENTITY_PREVIEW_METADATA.CREATOR_NAME:
            return creatorName;
          case ENTITY_PREVIEW_METADATA.CREATED_AT:
            return fileDate;
          case ENTITY_PREVIEW_METADATA.DOMAIN:
            return file.url ? getUrlDomain(file.url) : null;
          case ENTITY_PREVIEW_METADATA.FILE_SIZE:
            return file.size ? formatFileSize(file.size) : null;
          default:
            return null;
        }
      });

      data = renderMetadata(icon, pieces);
    }
  }

  if (row.type === ENTITY_TYPE.UPLOAD) {
    const hasError = !!upload.error;
    const isUploadComplete =
      upload.status === "complete" && upload.progress === 1 && !hasError;
    let description = "Uploading...";

    if (isUploadComplete) description = "Uploading complete";
    if (upload.status === "processing")
      description = "Upload complete, now processing file...";
    if (hasError) description = "Failed";

    title = (
      <>
        <div className="entity-preview-upload">
          <div className="entity-preview-upload-name">
            {upload.file.fileName}
          </div>
          <span className="description">
            <div
              className={classNames("entity-preview-metadata", {
                error: hasError,
              })}
            >
              {description}
            </div>
          </span>
        </div>

        <div className="upload-row-ctas">
          {hasError &&
            // Currently, we only pass onRetry for dropbox uploads.
            // Meaning, we only want to show retry for files that are passed a callback fn.
            upload.onRetry && (
              <StatusIndicator
                errorMessage={upload.error}
                retryCallback={upload.onRetry} // Ensure retry functionality works for dropbox only
                isLoading={upload.progress < 1 && !upload.error}
                className="upload-status-indicator"
              />
            )}

          {upload.status === "processing" && !hasError && (
            <Tooltip title="Upload complete, now processing file...">
              <Button theme={BUTTON_THEME.ICON} className="processing-upload">
                <LoadingSpinner />
              </Button>
            </Tooltip>
          )}

          {isUploadComplete && (
            <Button
              type="button"
              className="success-upload"
              theme={BUTTON_THEME.ICON}
              size={BUTTON_SIZE.XSMALL}
            >
              <SuccessSVG />
            </Button>
          )}

          {!isUploadComplete &&
            upload.status !== "processing" &&
            upload.cancelUpload && (
              <Button
                type="button"
                className="cancel-upload"
                theme={BUTTON_THEME.ICON}
                size={BUTTON_SIZE.XSMALL}
                onClick={upload.cancelUpload}
              >
                <CloseSVG />
              </Button>
            )}
        </div>
      </>
    );
    data = (
      <ProgressBar
        progress={
          hasError
            ? typeof upload.progress === "number" && upload.progress > 0
              ? upload.progress
              : 0.1
            : upload.progress
        }
        className="highnote-preview-progress-bar"
        hasError={hasError}
      />
    );
  }
  const handleOnClick = () => {
    if (row.confirmOnClick) {
      row
        .confirmOnClick(confirm)
        .then(() => {
          row.onConfirmCancel?.();
        })
        .catch(() => {
          row?.onClick?.();
        });
    } else {
      row?.onClick?.();
    }
  };

  const renderThumbnailPreview = useCallback(
    (onClick?: () => void) => (
      <>
        <ThumbnailPreview
          onClick={onClick} // onClick controls the Preview Title click
          hideThumbnail={hideThumbnail}
          iconSource={
            row.type === ENTITY_TYPE.SPACE
              ? (row.entity as Space).artworkUrl || source
              : source
          }
          iconFallback={fallback}
          title={title}
          description={data}
          row={row}
        />
        {renderConfirmation}
      </>
    ),
    [source, fallback, title, data, renderConfirmation, row],
  );

  if (isMobile && row.type !== ENTITY_TYPE.UPLOAD) {
    if (row.type === ENTITY_TYPE.TRACK) {
      return (
        <TrackPreviewMobileWrapper row={row} version={version}>
          {renderThumbnailPreview()}
        </TrackPreviewMobileWrapper>
      );
    }

    const allowedOnClick =
      row.type === ENTITY_TYPE.SPACE || row.type === ENTITY_TYPE.TRACK_VERSION;

    return (
      <Button
        theme={BUTTON_THEME.ICON}
        size={BUTTON_SIZE.AUTO}
        className={classNames(
          "highnote-entity-preview highnote-entity-preview-button",
          className,
        )}
        // We don't want to allow or push for downloading files on mobile
        // which is their default click event. So only allow Space clicks.
        onClick={allowedOnClick ? handleOnClick : undefined}
        {...(version
          ? {
              "data-is-default-version": !!version.isDefaultVersion,
              "data-version-color-id": version.colorId,
            }
          : {})}
      >
        {renderThumbnailPreview()}
      </Button>
    );
  }

  /**
   * If the entire row is clickable, the row's onClick will fire — no need to
   * attach a separate handler to the title (would otherwise double-fire).
   */
  const thumbnailPreviewOnClick = row.entireRowClickable
    ? undefined
    : handleOnClick;

  return (
    <div
      className={classNames("highnote-entity-preview", className)}
      {...(version
        ? {
            "data-is-default-version": !!version.isDefaultVersion,
            "data-version-color-id": version.colorId,
          }
        : {})}
    >
      {renderThumbnailPreview(thumbnailPreviewOnClick)}
    </div>
  );
};

// This component was created separately to handle the mobile version of the TrackPreview
// which requires useTrackRow to handle the play/pause functionality.
// To avoid adding the useTrackRow hook to the EntityPreview component, we created this wrapper.
const TrackPreviewMobileWrapper = ({
  row,
  version,
  children,
}: PropsWithChildren<{
  row: EntityData;
  version?: Version;
}>) => {
  const track = row.entity as Track;
  const { handleOnTogglePlay } = useTrackRow(
    version?.track?.id || track.id,
    version ? track.id : undefined,
  );

  // On mobile, tapping a track row both loads it into the player AND navigates
  // to the track page (the same nav the row's onClick / EntityCTA carries),
  // rather than only toggling play.
  const handleClick = () => {
    handleOnTogglePlay();
    row.onClick?.();
  };

  return (
    <Button
      theme={BUTTON_THEME.ICON}
      size={BUTTON_SIZE.AUTO}
      className="highnote-entity-preview highnote-entity-preview-button"
      {...(version
        ? {
            "data-is-default-version": !!version.isDefaultVersion,
            "data-version-color-id": version.colorId,
          }
        : {})}
      onClick={handleClick}
    >
      {children}
    </Button>
  );
};
